import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import WhatsAppForm from '../components/WhatsAppForm';
import GuideSection from '../components/GuideSection';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  MessageCircle,
  FileWarning,
  ShieldCheck,
  Ban,
  UserCheck,
  Building,
  FileSearch,
  Clock,
  Phone,
  Award,
  Eye,
  FileText,
  AlertCircle,
  HelpCircle,
  Zap,
  XCircle,
  RefreshCw,
  Lock,
  CreditCard,
  Globe,
  Users,
  Pill,
  Banknote,
  Scale,
  ShieldOff,
} from 'lucide-react';
import { images, contactByLanguage } from '../data/mock';

const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const CompliancePage = () => {
  const { language, t } = useLanguage();

  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  const openWhatsApp = () => {
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const content = {
    es: {
      hero: {
        badge: 'Expertos en Compliance Google Ads',
        title: 'Recupera tu Cuenta Google Ads Suspendida',
        subtitle: 'Somos especialistas en resolver suspensiones, verificaciones Business Operation Verification, Unacceptable Business Practices y todo tipo de problemas de compliance con Google Ads. Recuperamos tu cuenta de forma rápida y segura.',
      },
      suspensionReasons: {
        title: 'Razones de Suspensión que Resolvemos',
        subtitle: 'Cada suspensión tiene detalles técnicos específicos que solo expertos pueden identificar y resolver',
        items: [
          { 
            icon: <RefreshCw className="w-6 h-6" />, 
            title: 'Circumventing Systems', 
            desc: 'Suspensión por "elusión de sistemas". Ocurre cuando Google detecta intentos de evadir su sistema de revisión mediante cloaking, redirects ocultos o manipulación de contenido.',
            color: googleColors.red,
          },
          { 
            icon: <Building className="w-6 h-6" />, 
            title: 'Business Operation Verification (BOV)', 
            desc: 'Verificación de operaciones comerciales. Google solicita documentación para probar la legitimidad de tu negocio: documentos legales, verificación de dominio, pruebas de operación.',
            color: googleColors.blue,
          },
          { 
            icon: <AlertCircle className="w-6 h-6" />, 
            title: 'Unacceptable Business Practices', 
            desc: 'Prácticas comerciales inaceptables. Marcado cuando Google considera que tu modelo de negocio no cumple con sus estándares de calidad o transparencia.',
            color: googleColors.yellow,
          },
          { 
            icon: <CreditCard className="w-6 h-6" />, 
            title: 'Suspicious Payment Activity', 
            desc: 'Actividad de pago sospechosa. Suspensión relacionada con métodos de pago, facturación irregular o actividad financiera inusual en la cuenta.',
            color: googleColors.red,
          },
          { 
            icon: <ShieldOff className="w-6 h-6" />, 
            title: 'Misleading Content / Misrepresentation', 
            desc: 'Contenido engañoso o declaraciones falsas. Cuando las landing pages o anuncios contienen información que Google considera inexacta o engañosa.',
            color: googleColors.yellow,
          },
          { 
            icon: <Users className="w-6 h-6" />, 
            title: 'Coordinated Deceptive Practices', 
            desc: 'Prácticas engañosas coordinadas. Suspensión por conexión con otras cuentas suspendidas o comportamiento coordinado sospechoso.',
            color: googleColors.red,
          },
          { 
            icon: <Lock className="w-6 h-6" />, 
            title: 'Policy Violation - Restricted Content', 
            desc: 'Violación de contenido restringido. Sectores como salud, finanzas, legal, alcohol, juegos requieren certificaciones y cumplimiento estricto.',
            color: googleColors.blue,
          },
          { 
            icon: <Globe className="w-6 h-6" />, 
            title: 'Destination Not Working / Mismatch', 
            desc: 'Destino no funciona o no coincide. Problemas con URLs, dominios, redirects o contenido que no coincide con el anuncio.',
            color: googleColors.green,
          },
        ],
      },
      rejectedAds: {
        title: 'Anuncios Rechazados - Ejemplos Específicos',
        subtitle: 'Rechazos comunes que parecen imposibles de resolver pero tienen solución',
        items: [
          {
            title: 'Call Directory / Servicios de Directorio',
            desc: 'Anuncios de servicios telefónicos, directorios de números o intermediación de llamadas. Política muy estricta que requiere demostrar valor añadido real.',
            solution: 'Documentamos el valor añadido del servicio y modificamos el enfoque publicitario.',
          },
          {
            title: 'Third-Party Consumer Technical Support',
            desc: 'Soporte técnico para productos de terceros (Microsoft, Apple, etc.). Requiere certificación oficial o cambio de estrategia.',
            solution: 'Obtenemos certificaciones necesarias o reenfocamos la campaña legalmente.',
          },
          {
            title: 'Financial Products (Crypto, Préstamos)',
            desc: 'Productos financieros, criptomonedas, préstamos personales. Regulaciones estrictas por país y tipo de producto.',
            solution: 'Verificamos licencias, adaptamos landing pages y solicitamos certificaciones.',
          },
          {
            title: 'Healthcare & Pharmaceuticals',
            desc: 'Productos de salud, suplementos, tratamientos médicos. Requiere certificación LegitScript en muchos casos.',
            solution: 'Gestionamos certificación LegitScript y adaptamos claims publicitarios.',
          },
          {
            title: 'Legal Services Restrictions',
            desc: 'Servicios legales con restricciones por país. Abogados, recuperación de deudas, servicios de inmigración.',
            solution: 'Verificamos regulaciones locales y adaptamos la estrategia de segmentación.',
          },
          {
            title: 'Gambling & Games of Skill',
            desc: 'Juegos de azar, apuestas, casinos online. Requiere licencias específicas por mercado.',
            solution: 'Verificamos licencias de juego y configuramos segmentación por mercados autorizados.',
          },
        ],
      },
      problems: {
        title: '¿Tu Cuenta Google Ads Tiene Problemas?',
        subtitle: 'Resolvemos todo tipo de suspensiones y rechazos',
        items: [
          { icon: <Ban className="w-7 h-7" />, title: 'Cuenta Suspendida', desc: 'Tu cuenta ha sido suspendida por violaciones de políticas.', color: googleColors.red },
          { icon: <FileWarning className="w-7 h-7" />, title: 'Anuncios Rechazados', desc: 'Tus anuncios no se aprueban por problemas de políticas.', color: googleColors.yellow },
          { icon: <Building className="w-7 h-7" />, title: 'Business Operation Verification', desc: 'Google te solicita completar la verificación de operaciones comerciales.', color: googleColors.blue },
          { icon: <UserCheck className="w-7 h-7" />, title: 'Verificación de Anunciante', desc: 'Necesitas verificar tu identidad como anunciante.', color: googleColors.green },
          { icon: <AlertCircle className="w-7 h-7" />, title: 'Unacceptable Business Practices', desc: 'Tu cuenta ha sido marcada por prácticas comerciales inaceptables.', color: googleColors.red },
          { icon: <Shield className="w-7 h-7" />, title: 'Otras Violaciones', desc: 'Cualquier otro problema de compliance con Google Ads.', color: googleColors.blue },
        ],
      },
      process: {
        title: 'Nuestro Proceso de Resolución',
        subtitle: 'Un método probado con más del 85% de éxito',
        steps: [
          { num: '01', title: 'Análisis Inicial', desc: 'Revisamos tu cuenta, identificamos la causa exacta y evaluamos opciones.', icon: <FileSearch className="w-6 h-6" /> },
          { num: '02', title: 'Estrategia de Apelación', desc: 'Diseñamos una estrategia personalizada basada en tu caso específico.', icon: <FileText className="w-6 h-6" /> },
          { num: '03', title: 'Preparación de Documentación', desc: 'Preparamos toda la documentación y redactamos la apelación profesionalmente.', icon: <Eye className="w-6 h-6" /> },
          { num: '04', title: 'Envío y Seguimiento', desc: 'Enviamos la apelación y hacemos seguimiento hasta obtener respuesta.', icon: <Clock className="w-6 h-6" /> },
        ],
      },
      stats: {
        title: 'Resultados que Hablan',
        items: [
          { value: '85%+', label: 'Tasa de éxito en apelaciones' },
          { value: '24-48h', label: 'Tiempo de respuesta inicial' },
          { value: '30+', label: 'Cuentas recuperadas' },
          { value: '100%', label: 'Confidencialidad garantizada' },
        ],
      },
      cta: {
        title: '¿Necesitas ayuda con tu cuenta Google Ads?',
        subtitle: 'Contáctanos ahora para una evaluación gratuita de tu caso',
        button: 'Contactar por WhatsApp',
      },
    },
    fr: {
      hero: {
        badge: 'Experts en Conformité Google Ads',
        title: 'Récupérez Votre Compte Google Ads Suspendu',
        subtitle: 'Nous sommes spécialistes en résolution de suspensions, vérifications Business Operation Verification, Unacceptable Business Practices et tout type de problèmes de conformité Google Ads.',
      },
      suspensionReasons: {
        title: 'Raisons de Suspension que Nous Résolvons',
        subtitle: 'Chaque suspension a des détails techniques spécifiques que seuls des experts peuvent identifier et résoudre',
        items: [
          { icon: <RefreshCw className="w-6 h-6" />, title: 'Circumventing Systems', desc: 'Suspension pour "contournement des systèmes". Se produit quand Google détecte des tentatives d\'évasion de son système de révision.', color: googleColors.red },
          { icon: <Building className="w-6 h-6" />, title: 'Business Operation Verification (BOV)', desc: 'Vérification des opérations commerciales. Google demande une documentation pour prouver la légitimité de votre entreprise.', color: googleColors.blue },
          { icon: <AlertCircle className="w-6 h-6" />, title: 'Unacceptable Business Practices', desc: 'Pratiques commerciales inacceptables. Marqué quand Google considère que votre modèle d\'affaires ne répond pas à ses standards.', color: googleColors.yellow },
          { icon: <CreditCard className="w-6 h-6" />, title: 'Suspicious Payment Activity', desc: 'Activité de paiement suspecte. Suspension liée aux méthodes de paiement ou activité financière inhabituelle.', color: googleColors.red },
          { icon: <ShieldOff className="w-6 h-6" />, title: 'Misleading Content / Misrepresentation', desc: 'Contenu trompeur ou déclarations fausses dans les landing pages ou annonces.', color: googleColors.yellow },
          { icon: <Users className="w-6 h-6" />, title: 'Coordinated Deceptive Practices', desc: 'Pratiques trompeuses coordonnées. Suspension pour connexion avec d\'autres comptes suspendus.', color: googleColors.red },
          { icon: <Lock className="w-6 h-6" />, title: 'Policy Violation - Restricted Content', desc: 'Violation de contenu restreint. Secteurs comme santé, finances, légal nécessitent des certifications.', color: googleColors.blue },
          { icon: <Globe className="w-6 h-6" />, title: 'Destination Not Working / Mismatch', desc: 'Destination ne fonctionne pas ou ne correspond pas. Problèmes avec URLs ou redirects.', color: googleColors.green },
        ],
      },
      rejectedAds: {
        title: 'Annonces Refusées - Exemples Spécifiques',
        subtitle: 'Refus courants qui semblent impossibles à résoudre mais ont une solution',
        items: [
          { title: 'Call Directory / Services d\'Annuaire', desc: 'Annonces de services téléphoniques, annuaires de numéros. Politique très stricte.', solution: 'Nous documentons la valeur ajoutée du service et modifions l\'approche publicitaire.' },
          { title: 'Third-Party Consumer Technical Support', desc: 'Support technique pour produits tiers (Microsoft, Apple, etc.).', solution: 'Nous obtenons les certifications nécessaires ou réorientons la campagne.' },
          { title: 'Financial Products (Crypto, Prêts)', desc: 'Produits financiers, cryptomonnaies, prêts personnels.', solution: 'Nous vérifions les licences et adaptons les landing pages.' },
          { title: 'Healthcare & Pharmaceuticals', desc: 'Produits de santé, suppléments, traitements médicaux.', solution: 'Nous gérons la certification LegitScript et adaptons les claims.' },
          { title: 'Legal Services Restrictions', desc: 'Services juridiques avec restrictions par pays.', solution: 'Nous vérifions les réglementations locales et adaptons la stratégie.' },
          { title: 'Gambling & Games of Skill', desc: 'Jeux de hasard, paris, casinos en ligne.', solution: 'Nous vérifions les licences et configurons le ciblage par marchés autorisés.' },
        ],
      },
      problems: {
        title: 'Votre Compte Google Ads a des Problèmes ?',
        subtitle: 'Nous résolvons tous types de suspensions et refus',
        items: [
          { icon: <Ban className="w-7 h-7" />, title: 'Compte Suspendu', desc: 'Votre compte a été suspendu pour violations de politiques.', color: googleColors.red },
          { icon: <FileWarning className="w-7 h-7" />, title: 'Annonces Refusées', desc: 'Vos annonces ne sont pas approuvées.', color: googleColors.yellow },
          { icon: <Building className="w-7 h-7" />, title: 'Business Operation Verification', desc: 'Google vous demande de compléter la vérification BOV.', color: googleColors.blue },
          { icon: <UserCheck className="w-7 h-7" />, title: 'Vérification Annonceur', desc: 'Vous devez vérifier votre identité.', color: googleColors.green },
          { icon: <AlertCircle className="w-7 h-7" />, title: 'Unacceptable Business Practices', desc: 'Votre compte a été marqué pour pratiques inacceptables.', color: googleColors.red },
          { icon: <Shield className="w-7 h-7" />, title: 'Autres Violations', desc: 'Tout autre problème de conformité.', color: googleColors.blue },
        ],
      },
      process: {
        title: 'Notre Processus de Résolution',
        subtitle: 'Une méthode éprouvée avec plus de 85% de succès',
        steps: [
          { num: '01', title: 'Analyse Initiale', desc: 'Nous révisons votre compte et identifions la cause exacte.', icon: <FileSearch className="w-6 h-6" /> },
          { num: '02', title: 'Stratégie d\'Appel', desc: 'Nous concevons une stratégie personnalisée pour votre cas.', icon: <FileText className="w-6 h-6" /> },
          { num: '03', title: 'Préparation Documentation', desc: 'Nous préparons toute la documentation nécessaire.', icon: <Eye className="w-6 h-6" /> },
          { num: '04', title: 'Envoi et Suivi', desc: 'Nous envoyons l\'appel et assurons le suivi jusqu\'à la réponse.', icon: <Clock className="w-6 h-6" /> },
        ],
      },
      stats: {
        title: 'Résultats qui Parlent',
        items: [
          { value: '85%+', label: 'Taux de succès des appels' },
          { value: '24-48h', label: 'Temps de réponse initial' },
          { value: '30+', label: 'Comptes récupérés' },
          { value: '100%', label: 'Confidentialité garantie' },
        ],
      },
      cta: {
        title: 'Besoin d\'aide avec votre compte Google Ads ?',
        subtitle: 'Contactez-nous maintenant pour une évaluation gratuite',
        button: 'Contacter par WhatsApp',
      },
    },
    en: {
      hero: {
        badge: 'Google Ads Compliance Experts',
        title: 'Recover Your Suspended Google Ads Account',
        subtitle: 'We specialize in resolving suspensions, Business Operation Verification, Unacceptable Business Practices and all types of Google Ads compliance issues.',
      },
      suspensionReasons: {
        title: 'Suspension Reasons We Resolve',
        subtitle: 'Each suspension has specific technical details that only experts can identify and resolve',
        items: [
          { icon: <RefreshCw className="w-6 h-6" />, title: 'Circumventing Systems', desc: 'Suspension for attempting to evade Google\'s review system through cloaking, hidden redirects, or content manipulation.', color: googleColors.red },
          { icon: <Building className="w-6 h-6" />, title: 'Business Operation Verification (BOV)', desc: 'Business operations verification. Google requests documentation to prove your business legitimacy.', color: googleColors.blue },
          { icon: <AlertCircle className="w-6 h-6" />, title: 'Unacceptable Business Practices', desc: 'Flagged when Google considers your business model doesn\'t meet their quality or transparency standards.', color: googleColors.yellow },
          { icon: <CreditCard className="w-6 h-6" />, title: 'Suspicious Payment Activity', desc: 'Suspension related to payment methods, irregular billing, or unusual financial activity.', color: googleColors.red },
          { icon: <ShieldOff className="w-6 h-6" />, title: 'Misleading Content / Misrepresentation', desc: 'When landing pages or ads contain information Google considers inaccurate or deceptive.', color: googleColors.yellow },
          { icon: <Users className="w-6 h-6" />, title: 'Coordinated Deceptive Practices', desc: 'Suspension for connection with other suspended accounts or suspicious coordinated behavior.', color: googleColors.red },
          { icon: <Lock className="w-6 h-6" />, title: 'Policy Violation - Restricted Content', desc: 'Sectors like healthcare, finance, legal, gambling require certifications and strict compliance.', color: googleColors.blue },
          { icon: <Globe className="w-6 h-6" />, title: 'Destination Not Working / Mismatch', desc: 'Issues with URLs, domains, redirects, or content that doesn\'t match the ad.', color: googleColors.green },
        ],
      },
      rejectedAds: {
        title: 'Rejected Ads - Specific Examples',
        subtitle: 'Common rejections that seem impossible to resolve but have solutions',
        items: [
          { title: 'Call Directory / Directory Services', desc: 'Phone service ads, number directories, or call intermediation. Very strict policy requiring real added value.', solution: 'We document the service\'s added value and modify the advertising approach.' },
          { title: 'Third-Party Consumer Technical Support', desc: 'Technical support for third-party products (Microsoft, Apple, etc.). Requires official certification.', solution: 'We obtain necessary certifications or legally refocus the campaign.' },
          { title: 'Financial Products (Crypto, Loans)', desc: 'Financial products, cryptocurrencies, personal loans. Strict regulations by country and product type.', solution: 'We verify licenses, adapt landing pages, and request certifications.' },
          { title: 'Healthcare & Pharmaceuticals', desc: 'Health products, supplements, medical treatments. Often requires LegitScript certification.', solution: 'We manage LegitScript certification and adapt advertising claims.' },
          { title: 'Legal Services Restrictions', desc: 'Legal services with country-specific restrictions. Lawyers, debt collection, immigration services.', solution: 'We verify local regulations and adapt targeting strategy.' },
          { title: 'Gambling & Games of Skill', desc: 'Gambling, betting, online casinos. Requires specific licenses per market.', solution: 'We verify gaming licenses and configure targeting for authorized markets.' },
        ],
      },
      problems: {
        title: 'Is Your Google Ads Account Having Problems?',
        subtitle: 'We resolve all types of suspensions and rejections',
        items: [
          { icon: <Ban className="w-7 h-7" />, title: 'Account Suspended', desc: 'Your account has been suspended for policy violations.', color: googleColors.red },
          { icon: <FileWarning className="w-7 h-7" />, title: 'Ads Rejected', desc: 'Your ads are not being approved due to policy issues.', color: googleColors.yellow },
          { icon: <Building className="w-7 h-7" />, title: 'Business Operation Verification', desc: 'Google is requesting you complete business verification.', color: googleColors.blue },
          { icon: <UserCheck className="w-7 h-7" />, title: 'Advertiser Verification', desc: 'You need to verify your identity as an advertiser.', color: googleColors.green },
          { icon: <AlertCircle className="w-7 h-7" />, title: 'Unacceptable Business Practices', desc: 'Your account has been flagged for unacceptable practices.', color: googleColors.red },
          { icon: <Shield className="w-7 h-7" />, title: 'Other Violations', desc: 'Any other Google Ads compliance issue.', color: googleColors.blue },
        ],
      },
      process: {
        title: 'Our Resolution Process',
        subtitle: 'A proven method with over 85% success rate',
        steps: [
          { num: '01', title: 'Initial Analysis', desc: 'We review your account and identify the exact cause.', icon: <FileSearch className="w-6 h-6" /> },
          { num: '02', title: 'Appeal Strategy', desc: 'We design a personalized strategy for your specific case.', icon: <FileText className="w-6 h-6" /> },
          { num: '03', title: 'Documentation Preparation', desc: 'We prepare all necessary documentation professionally.', icon: <Eye className="w-6 h-6" /> },
          { num: '04', title: 'Submission & Follow-up', desc: 'We submit the appeal and follow up until we get a response.', icon: <Clock className="w-6 h-6" /> },
        ],
      },
      stats: {
        title: 'Results That Speak',
        items: [
          { value: '85%+', label: 'Appeal success rate' },
          { value: '24-48h', label: 'Initial response time' },
          { value: '30+', label: 'Accounts recovered' },
          { value: '100%', label: 'Confidentiality guaranteed' },
        ],
      },
      cta: {
        title: 'Need help with your Google Ads account?',
        subtitle: 'Contact us now for a free case evaluation',
        button: 'Contact via WhatsApp',
      },
    },
  };

  const c = content[language] || content.es;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full" style={{ backgroundColor: googleColors.blue }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full" style={{ backgroundColor: googleColors.red }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-6">
                <Shield className="w-4 h-4 mr-2" />
                {c.hero.badge}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">
                {c.hero.title}
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {c.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={openWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg"
                  size="lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="border-2 px-8 py-6 text-lg"
                  style={{ borderColor: googleColors.blue, color: googleColors.blue }}
                  size="lg"
                  asChild
                >
                  <Link to={`/${language}/contacto`}>
                    {c.cta.button}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src={images.complianceIllustration}
                alt="Google Ads Compliance"
                className="w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <GuideSection location="compliance" articleLink />

      {/* Suspension Reasons */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.suspensionReasons.title}</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">{c.suspensionReasons.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.suspensionReasons.items.map((item, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${item.color}15`, color: item.color }}
                  >
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Rejected Ads Examples */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.rejectedAds.title}</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">{c.rejectedAds.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {c.rejectedAds.items.map((item, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <XCircle className="w-6 h-6 mr-3" style={{ color: googleColors.red }} />
                    <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{item.desc}</p>
                  <div className="flex items-start p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: googleColors.green }} />
                    <p className="text-sm text-green-800">{item.solution}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.process.title}</h2>
            <p className="text-lg text-slate-600">{c.process.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {c.process.steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-black text-slate-100 mb-4">{step.num}</div>
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${googleColors.blue}15`, color: googleColors.blue }}
                >
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20" style={{ backgroundColor: googleColors.blue }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">{c.stats.title}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {c.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl sm:text-5xl font-black text-white mb-2">{stat.value}</div>
                <div className="text-blue-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">{c.cta.title}</h2>
            <p className="text-lg text-slate-600">{c.cta.subtitle}</p>
          </div>
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8">
              <WhatsAppForm />
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CompliancePage;
